import { useState, useCallback, useEffect, useRef } from 'react';
import { trackChatMessageSent, trackChatPromptClicked, trackChatFeedback, trackEngagementPromptClicked } from '../utils/analytics';

// Function URLs for HTTP endpoints
const CHAT_FUNCTION_URL = 'https://us-central1-i-am-assessment.cloudfunctions.net/askWebsiteChatHTTP';
const FEEDBACK_FUNCTION_URL = 'https://us-central1-i-am-assessment.cloudfunctions.net/submitChatFeedbackHTTP';

// Default follow-up prompts when model doesn't provide any
const DEFAULT_FOLLOW_UP_QUESTIONS = [
  "What would you like to learn more about?",
  "Ready to see how this applies to your team?",
  "Have a specific challenge in mind?",
  "Want to explore our assessment tool?",
];

// Strip "Assistant:" prefix from responses (model sometimes echoes this)
function cleanAssistantResponse(response: string): string {
  return response.replace(/^(Assistant:\s*)/i, '').trim();
}

// Parse follow-up questions from the response
// Expected format: [FOLLOW_UP: "question 1" | "question 2"]
function parseFollowUpQuestions(response: string): { cleanContent: string; questions: string[] } {
  // Pattern to match follow-up questions marker
  const followUpPattern = /\[FOLLOW_UP:\s*"([^"]+)"\s*\|\s*"([^"]+)"\s*\]/i;
  const match = response.match(followUpPattern);

  if (match) {
    // Remove the follow-up marker from the response
    const cleanContent = response.replace(followUpPattern, '').trim();
    const questions = [match[1], match[2]].filter(q => q && q.length > 0);
    return { cleanContent, questions };
  }

  return { cleanContent: response, questions: [] };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  feedback?: 'up' | 'down';
  followUpQuestions?: string[];
}

interface UseWebsiteChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  sendPrompt: (prompt: string) => Promise<void>;
  submitFeedback: (messageIndex: number, feedback: 'up' | 'down') => Promise<void>;
  clearError: () => void;
  sessionId: string;
  showEngagementPrompts: boolean;
  currentFollowUpQuestions: string[];
  handleEngagementPromptClick: (prompt: string) => void;
  onUserInputChange: () => void;
}

// Generate a unique session ID
function generateSessionId(): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `chat_${timestamp}_${randomPart}`;
}

// Get or create session ID from sessionStorage
function getSessionId(): string {
  const STORAGE_KEY = 'neupath_chat_session';

  if (typeof sessionStorage !== 'undefined') {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const newId = generateSessionId();
    sessionStorage.setItem(STORAGE_KEY, newId);
    return newId;
  }

  return generateSessionId();
}

export function useWebsiteChat(): UseWebsiteChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState(() => getSessionId());
  const [showEngagementPrompts, setShowEngagementPrompts] = useState(false);
  const [currentFollowUpQuestions, setCurrentFollowUpQuestions] = useState<string[]>([]);
  const promptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followUpIndexRef = useRef(0); // For cycling through default prompts

  // Load conversation history from sessionStorage on mount
  useEffect(() => {
    const HISTORY_KEY = 'neupath_chat_history';
    if (typeof sessionStorage !== 'undefined') {
      const saved = sessionStorage.getItem(HISTORY_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setMessages(parsed.map((m: { role: string; content: string; timestamp: string; feedback?: 'up' | 'down' }) => {
            // Clean assistant messages that may have old FOLLOW_UP markers
            let content = m.content;
            if (m.role === 'assistant') {
              content = cleanAssistantResponse(content);
              const { cleanContent } = parseFollowUpQuestions(content);
              content = cleanContent;
            }
            return {
              ...m,
              content,
              timestamp: new Date(m.timestamp),
            };
          }));
        } catch {
          // Ignore invalid saved data
        }
      }
    }
  }, []);

  // Save conversation to sessionStorage when messages change
  useEffect(() => {
    const HISTORY_KEY = 'neupath_chat_history';
    if (typeof sessionStorage !== 'undefined' && messages.length > 0) {
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  const sendMessage = useCallback(async (message: string, source: 'typed' | 'suggested_prompt' | 'follow_up_prompt' = 'typed') => {
    if (!message.trim()) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: message.trim(),
      timestamp: new Date(),
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    // Track analytics
    trackChatMessageSent(sessionId, messages.length + 1);

    try {
      // Build conversation history for context
      // Strip out the follow-up markers from assistant responses before sending
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.role === 'assistant'
          ? m.content.replace(/\[FOLLOW_UP:.*?\]/gi, '').trim()
          : m.content,
      }));

      const response = await fetch(CHAT_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            message: message.trim(),
            sessionId,
            conversationHistory,
            messageSource: source,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message || 'Unknown error');
      }

      // Process the response: strip "Assistant:" prefix and extract follow-up questions
      let responseContent = cleanAssistantResponse(data.data.response);
      const { cleanContent, questions } = parseFollowUpQuestions(responseContent);
      responseContent = cleanContent;

      // Determine follow-up questions to show
      let followUps: string[];
      if (questions.length >= 2) {
        // Use model-provided questions
        followUps = questions.slice(0, 2);
      } else {
        // Use defaults, cycling through them
        const idx = followUpIndexRef.current;
        followUps = [
          DEFAULT_FOLLOW_UP_QUESTIONS[idx % DEFAULT_FOLLOW_UP_QUESTIONS.length],
          DEFAULT_FOLLOW_UP_QUESTIONS[(idx + 1) % DEFAULT_FOLLOW_UP_QUESTIONS.length],
        ];
        followUpIndexRef.current = (idx + 2) % DEFAULT_FOLLOW_UP_QUESTIONS.length;
      }

      setCurrentFollowUpQuestions(followUps);

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: responseContent,
        timestamp: new Date(),
        followUpQuestions: followUps,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);

      // Remove the user message if the request failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  }, [messages, sessionId]);

  const sendPrompt = useCallback(async (prompt: string) => {
    trackChatPromptClicked(prompt);
    await sendMessage(prompt, 'suggested_prompt');
  }, [sendMessage]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Submit feedback for a specific message
  const submitFeedback = useCallback(async (messageIndex: number, feedback: 'up' | 'down') => {
    // Optimistic UI update
    setMessages(prev => prev.map((msg, idx) =>
      idx === messageIndex ? { ...msg, feedback } : msg
    ));

    // Track analytics
    trackChatFeedback(sessionId, messageIndex, feedback);

    try {
      await fetch(FEEDBACK_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            sessionId,
            messageIndex,
            feedback,
          },
        }),
      });
      // Response doesn't matter for optimistic UI
    } catch {
      // Silently fail - feedback is non-critical
      console.warn('Failed to submit feedback');
    }
  }, [sessionId]);

  // Cancel engagement prompts timer
  const cancelPromptTimer = useCallback(() => {
    if (promptTimerRef.current) {
      clearTimeout(promptTimerRef.current);
      promptTimerRef.current = null;
    }
    setShowEngagementPrompts(false);
  }, []);

  // Called when user starts typing or interacts with input
  const onUserInputChange = useCallback(() => {
    cancelPromptTimer();
  }, [cancelPromptTimer]);

  // Handle engagement prompt click
  const handleEngagementPromptClick = useCallback((prompt: string) => {
    trackEngagementPromptClicked(sessionId, prompt);
    cancelPromptTimer();
    sendMessage(prompt, 'follow_up_prompt');
  }, [sessionId, cancelPromptTimer, sendMessage]);

  // Start engagement prompt timer when assistant responds
  // We watch isLoading and messages to detect when response arrives
  useEffect(() => {
    // When loading finishes and last message is from assistant, start timer
    if (!isLoading && messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      promptTimerRef.current = setTimeout(() => {
        setShowEngagementPrompts(true);
      }, 5000);
    }

    // Cleanup timer when effect re-runs or unmounts
    return () => {
      if (promptTimerRef.current) {
        clearTimeout(promptTimerRef.current);
        promptTimerRef.current = null;
      }
    };
  }, [isLoading, messages]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    sendPrompt,
    submitFeedback,
    clearError,
    sessionId,
    showEngagementPrompts,
    currentFollowUpQuestions,
    handleEngagementPromptClick,
    onUserInputChange,
  };
}

// Suggested prompts for the chat widget
export const SUGGESTED_PROMPTS = [
  "What is the AI Readiness Assessment?",
  "Why do AI transformations fail?",
  "What are the four AI adoption profiles?",
  "How can you help my team?",
];
